/**
 * SVG Occupancy Grid — content-aware label placement for elevation views.
 *
 * Parses cached SVG line-drawings and builds a coarse grid that marks which
 * cells contain drawn content. This lets labels be placed in empty regions
 * inside an object's bounding box instead of relying on AABB overlap alone.
 */

// Module-level cache: URL → { cells, cols, rows }
const occupancyGridCache = new Map();

/* ------------------------------------------------------------------ */
/*  1. parseSvgLines                                                   */
/* ------------------------------------------------------------------ */

/**
 * Extract <line> elements and the viewBox from a raw SVG string.
 * Handles optional <g transform="..."> wrappers with scale / rotate.
 *
 * @param {string} rawSvg
 * @returns {{ lines: {x1:number,y1:number,x2:number,y2:number}[], viewBox: {x:number,y:number,w:number,h:number} } | null}
 */
export const parseSvgLines = (rawSvg) => {
  if (!rawSvg) return null;

  // --- viewBox ---
  const vbMatch = rawSvg.match(/viewBox="([^"]+)"/);
  if (!vbMatch) return null;
  const vbParts = vbMatch[1]
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (vbParts.length < 4 || vbParts.some(isNaN)) return null;
  const viewBox = {
    x: vbParts[0],
    y: vbParts[1],
    w: vbParts[2],
    h: vbParts[3],
  };
  if (viewBox.w === 0 || viewBox.h === 0) return null;

  // --- detect group transform ---
  // Limited set: scale(-1,1) for "back", rotate(180) for "bottom"
  let transformFn = null;
  const gTransform = rawSvg.match(/<g[^>]*\btransform="([^"]+)"/);
  if (gTransform) {
    const t = gTransform[1].trim();
    const scaleMatch = t.match(/scale\(\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\)/);
    const rotateMatch = t.match(/rotate\(\s*([-\d.]+)\s*\)/);
    if (scaleMatch) {
      const sx = Number(scaleMatch[1]);
      const sy = Number(scaleMatch[2]);
      transformFn = (x, y) => [x * sx, y * sy];
    } else if (rotateMatch) {
      const deg = Number(rotateMatch[1]);
      const rad = (deg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      transformFn = (x, y) => [x * cos - y * sin, x * sin + y * cos];
    }
  }

  // --- extract <line> elements ---
  const lineRegex =
    /<line\s[^>]*?x1="([^"]+)"[^>]*?y1="([^"]+)"[^>]*?x2="([^"]+)"[^>]*?y2="([^"]+)"/g;
  const lines = [];
  let m;
  while ((m = lineRegex.exec(rawSvg)) !== null) {
    let x1 = Number(m[1]);
    let y1 = Number(m[2]);
    let x2 = Number(m[3]);
    let y2 = Number(m[4]);
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;

    if (transformFn) {
      [x1, y1] = transformFn(x1, y1);
      [x2, y2] = transformFn(x2, y2);
    }
    lines.push({ x1, y1, x2, y2 });
  }

  return { lines, viewBox };
};

/* ------------------------------------------------------------------ */
/*  2. buildOccupancyGrid                                              */
/* ------------------------------------------------------------------ */

/**
 * Build a coarse occupancy grid from parsed SVG lines.
 * Each cell is 1 if any drawn content overlaps it, 0 otherwise.
 *
 * @param {{ x1:number, y1:number, x2:number, y2:number }[]} lines
 * @param {{ x:number, y:number, w:number, h:number }} viewBox
 * @param {number} [cols=8]
 * @param {number} [rows=8]
 * @returns {{ cells: Uint8Array, cols: number, rows: number }}
 */
export const buildOccupancyGrid = (lines, viewBox, cols = 12, rows = 12) => {
  const cells = new Uint8Array(cols * rows);
  const { x: vx, y: vy, w: vw, h: vh } = viewBox;

  const mark = (col, row) => {
    if (col >= 0 && col < cols && row >= 0 && row < rows) {
      cells[row * cols + col] = 1;
    }
  };

  for (const { x1, y1, x2, y2 } of lines) {
    // Map endpoints from viewBox space to grid cell coords
    const c1 = ((x1 - vx) / vw) * cols;
    const r1 = ((y1 - vy) / vh) * rows;
    const c2 = ((x2 - vx) / vw) * cols;
    const r2 = ((y2 - vy) / vh) * rows;

    // Walk the line segment with parametric stepping
    const dc = c2 - c1;
    const dr = r2 - r1;
    const steps = Math.max(Math.abs(dc), Math.abs(dr), 1);
    const numSteps = Math.ceil(steps) * 2; // oversample for accuracy

    for (let s = 0; s <= numSteps; s++) {
      const t = s / numSteps;
      const c = Math.floor(c1 + dc * t);
      const r = Math.floor(r1 + dr * t);
      mark(c, r);
    }
  }

  return { cells, cols, rows };
};

/* ------------------------------------------------------------------ */
/*  3. getOccupancyGrid                                                */
/* ------------------------------------------------------------------ */

/**
 * Build a synthetic grid for fallback box elements (no SVG projections).
 * Only border cells are occupied — the interior is empty, allowing labels
 * to be placed inside outlined rectangles.
 */
export const buildBorderGrid = (cols = 12, rows = 12) => {
  const cells = new Uint8Array(cols * rows);
  for (let c = 0; c < cols; c++) {
    cells[c] = 1; // top row
    cells[(rows - 1) * cols + c] = 1; // bottom row
  }
  for (let r = 0; r < rows; r++) {
    cells[r * cols] = 1; // left column
    cells[r * cols + (cols - 1)] = 1; // right column
  }
  return { cells, cols, rows };
};

/**
 * Return (or build+cache) an occupancy grid for the given SVG URL.
 *
 * @param {string} url           SVG projection URL
 * @param {Map}    svgCacheRef   The module-level svgCache from RenderWallElevation
 * @param {number} [cols=8]
 * @param {number} [rows=8]
 * @returns {{ cells: Uint8Array, cols: number, rows: number } | null}
 */
export const getOccupancyGrid = (url, svgCacheRef, cols = 12, rows = 12) => {
  if (!url) return null;

  // Check grid cache
  if (occupancyGridCache.has(url)) {
    return occupancyGridCache.get(url);
  }

  // Need raw SVG from the fetch cache
  const rawSvg = svgCacheRef.get(url);
  if (!rawSvg) return null; // SVG not fetched yet — caller falls back to AABB

  const parsed = parseSvgLines(rawSvg);
  if (!parsed || parsed.lines.length === 0) return null;

  const grid = buildOccupancyGrid(parsed.lines, parsed.viewBox, cols, rows);
  occupancyGridCache.set(url, grid);
  return grid;
};

/* ------------------------------------------------------------------ */
/*  4. labelOverlapsOccupied                                           */
/* ------------------------------------------------------------------ */

/**
 * Check whether a label rect overlaps any occupied cells of the object's grid.
 *
 * Both rects are in pixel coordinates. The grid maps onto objRect's bounds.
 *
 * @param {{ x:number, y:number, w:number, h:number }} labelRect  Label in px
 * @param {{ x:number, y:number, w:number, h:number }} objRect    Object bbox in px
 * @param {{ cells: Uint8Array, cols: number, rows: number }} grid
 * @param {number} [threshold=0] Fraction of cells that must be occupied to count
 *   as overlap. 0 = any occupied cell triggers overlap (strict, for neighbors).
 *   Higher values (e.g. 0.4) allow labels to slightly overlap edge lines.
 * @returns {boolean}
 */
export const labelOverlapsOccupied = (
  labelRect,
  objRect,
  grid,
  threshold = 0
) => {
  if (objRect.w === 0 || objRect.h === 0) return false;

  const { cols, rows, cells } = grid;

  // Map label rect corners into grid cell range
  const colStart = Math.floor(((labelRect.x - objRect.x) / objRect.w) * cols);
  const colEnd = Math.ceil(
    ((labelRect.x + labelRect.w - objRect.x) / objRect.w) * cols
  );
  const rowStart = Math.floor(((labelRect.y - objRect.y) / objRect.h) * rows);
  const rowEnd = Math.ceil(
    ((labelRect.y + labelRect.h - objRect.y) / objRect.h) * rows
  );

  // Clamp to grid bounds
  const c0 = Math.max(0, colStart);
  const c1 = Math.min(cols, colEnd);
  const r0 = Math.max(0, rowStart);
  const r1 = Math.min(rows, rowEnd);

  // Strict mode: any occupied cell = overlap
  if (threshold <= 0) {
    for (let r = r0; r < r1; r++) {
      for (let c = c0; c < c1; c++) {
        if (cells[r * cols + c]) return true;
      }
    }
    return false;
  }

  // Threshold mode: count fraction of occupied cells
  let total = 0;
  let occ = 0;
  for (let r = r0; r < r1; r++) {
    for (let c = c0; c < c1; c++) {
      total++;
      if (cells[r * cols + c]) occ++;
    }
  }
  if (total === 0) return false;
  return occ / total > threshold;
};
